import {
  IFilter,
  IFiltersValues,
  IGetSetFiltersValues,
  TFilterValue,
  PerformanceFilterOptionTypeEnum,
  filterNotEmptyValues,
  hasFilterValue,
  makeFilterValueFromValue,
  IProperty,
  IPageFilterProps,
} from '@diana/core';
import { castArray, toSafeInteger, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { FilterType } from '../stores/useFilters';
import { FiltersRenderer } from '../components/Filters/FiltersRenderer';
import { TimeRangeFilter } from '../components/Filters/TimeRangeFilter';
import { TimeRangeFilterValue, TimeRangeDateTimeValue } from '../components/Filters/FilterValue';
import { IExtendedFiltersValues } from '../utils/convertFiltersToQuery';
import { Box } from '@mui/material';
import { updateBrowserQueryParams } from '../utils/urlUtils';

export type IExtendedPageFilterProps = Omit<IPageFilterProps, 'filtersValues'> & {
  filtersValues: IExtendedFiltersValues;
  setFiltersValues: (filtersValues: IExtendedFiltersValues) => void;
};

export function convertDateValue(value: string, transformer: 'startOf' | 'endOf' = 'startOf') {
  const safeValue = toSafeInteger(value);
  return DateTime.now().minus({ days: safeValue })[transformer]('day').toISO();
}

export const normalizeDateRange = (rangeValue: any): { value: string; displayName: string }[] => {
  const [from, to] = castArray(rangeValue);
  const fromValue = DateTime.fromISO(from as string)?.isValid ? from : convertDateValue(from);
  const toValue = to
    ? DateTime.fromISO(to as string)?.isValid
      ? to
      : convertDateValue(to)
    : DateTime.now().endOf('day').toISO();

  return [
    { value: fromValue, displayName: fromValue },
    { value: toValue, displayName: toValue },
  ];
};

export function normalizeFiltersValues({
  filtersValues,
  availableFilters,
}: {
  filtersValues: any;
  availableFilters: IFilter[];
}) {
  const normalizedFiltersValues: IFiltersValues = {};
  for (let key in filtersValues) {
    if (
      availableFilters.some(
        (i) =>
          i.property.key === key && i.property.type === PerformanceFilterOptionTypeEnum.DATE_RANGE,
      )
    ) {
      const rawValue = filtersValues[key] ?? [];
      normalizedFiltersValues[key] = normalizeDateRange(rawValue);
    } else {
      normalizedFiltersValues[key] = filtersValues[key].map((filterValue: TFilterValue) => ({
        value: filterValue,
        displayName: filterValue!.toString(),
      }));
    }
  }
  return normalizedFiltersValues;
}

export const createPropertyToFilter = (
  property: IProperty,
  setFilterValues: (filtersValues: IExtendedFiltersValues) => void,
) => {
  if (property.key === FilterType.TIME_RANGE) {
    return {
      property,
      renderFilterValue: ({ filter, filterValue }: { filter: IFilter; filterValue: any }) => {
        if (filterValue[0]?.preset) {
          return (
            <TimeRangeFilterValue
              hasValue={true}
              value={[
                {
                  value: filterValue[0].preset,
                  displayName: 'Custom',
                },
              ]}
              preset={filterValue[0].preset}
              type={filter.property.type}
              title={filter.property.displayName}
            />
          );
        } else {
          return (
            <TimeRangeDateTimeValue
              hasValue={hasFilterValue(filterValue)}
              value={[
                {
                  value: filterValue[0]?.displayName,
                  displayName: 'Custom',
                },
                {
                  value: filterValue[1]?.displayName,
                  displayName: 'Custom',
                },
              ]}
              type={filter.property.type}
              title={filter.property.displayName}
            />
          );
        }
      },
      renderFilter: (props: any) => (
        <TimeRangeFilter {...props} setFiltersValues={setFilterValues} />
      ),
    };
  }

  return {
    property,
    renderFilter: (props: IPageFilterProps): ReactElement => {
      return (
        <Box
          sx={{
            '.MuiListItem-root p': {
              maxWidth: '355px',
            },
          }}
        >
          <FiltersRenderer filterProps={{ ...props, width: '355px' }} />
        </Box>
      );
    },
  };
};

export function useOnFilterChange({ filtersValues, setFiltersValues }: IGetSetFiltersValues) {
  return useCallback(
    ({ filter, value, rawValue }: { filter: IFilter; value: any; rawValue?: any }) => {
      if (hasFilterValue([makeFilterValueFromValue(value)])) {
        const isRawValueArray = Array.isArray(rawValue);
        const newFiltersValues = {
          ...filtersValues,
          [filter.property.key]: castArray(value).map((value, i) => {
            if (typeof value === 'object') {
              return {
                value: value.date,
                displayName: isRawValueArray ? rawValue[i].displayName ?? value.date : value.date,
                timezone: value.timezone,
                ...(value.preset && { preset: value.preset }),
              };
            }
            return {
              value,
              displayName: isRawValueArray ? rawValue[i].displayName ?? value : value,
            };
          }),
        };
        setFiltersValues(newFiltersValues);
      } else {
        const newFiltersValues = { ...filtersValues };
        delete newFiltersValues[filter.property.key];
        setFiltersValues(newFiltersValues);
      }
    },
    [filtersValues, setFiltersValues],
  );
}

export function useFilters({
  availableFilters,
  createPropertyToFilter,
  filtersValues,
  setFiltersValues,
  defaultFiltersValues = {},
}: IUseFiltersProps) {
  
  useEffect(() => {
    updateBrowserQueryParams(filtersValues);
  }, [filtersValues, setFiltersValues]);

  const onFilterChange = useOnFilterChange({
    filtersValues,
    setFiltersValues,
  });

  const defaultAvailableFilters = useMemo(() => {
    return availableFilters.map((value: IFilter) => createPropertyToFilter(value.property));
  }, [createPropertyToFilter, availableFilters]);

  return {
    defaultData: {
      availableFilters: defaultAvailableFilters,
      filtersValuesFromConfig: filterNotEmptyValues({
        ...defaultFiltersValues,
      }),
      filters: uniq([
        ...Object.keys({
          ...defaultFiltersValues,
        }),
      ]),
    },
    onFilterChange,
  };
}

export interface IUseFiltersProps {
  createPropertyToFilter: any;
  filtersValues: IFiltersValues;
  setFiltersValues: (values: IFiltersValues) => void;
  availableFilters: IFilter[];
  defaultFiltersValues?: IFiltersValues;
}
